import { useState, useEffect } from 'react';
import { Link } from '@tanstack/react-router';

const API = import.meta.env.VITE_API_URL || '/api';

type BackupType = 'manual' | 'auto';
type BackupStatus = 'completed' | 'restored' | 'failed' | string;

interface Backup {
  id: string;
  type: BackupType;
  status: BackupStatus;
  file_size: number;
  tables_count: number;
  records_count: number;
  created_at: string;
  notes?: string | null;
}

interface BackupSettings {
  backup_enabled: boolean;
  backup_retention_days: number;
}

function formatBytes(bytes: number | undefined): string {
  const size = Number(bytes);
  if (!Number.isFinite(size) || size <= 0) return '-';
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(2)} MB`;
}

function formatDateTime(value: string | undefined): string {
  if (!value) return '-';
  return new Date(value).toLocaleString('es-AR');
}

function formatNumber(value: number | undefined): string {
  return Number(value || 0).toLocaleString('es-AR');
}

function classNames(...classes: (string | false | undefined)[]) {
  return classes.filter(Boolean).join(' ');
}

function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center py-12">
      <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent" />
    </div>
  );
}

function ErrorBanner({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="bg-red-50 text-red-700 dark:text-red-300 p-4 rounded-lg mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
      <span className="text-sm font-medium">⚠️ {message}</span>
      <button
        onClick={onRetry}
        className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors min-h-[44px]"
      >
        Reintentar
      </button>
    </div>
  );
}

function EmptyState({ message, action }: { message: string; action?: React.ReactNode }) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8 text-center">
      <p className="text-4xl mb-3">📭</p>
      <p className="text-gray-600 dark:text-gray-300 font-medium text-sm">{message}</p>
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

const statusLabels: Record<string, string> = {
  completed: 'Completado',
  restored: 'Restaurado',
  failed: 'Fallido',
};

export function Backups() {
  const userStr = localStorage.getItem('user');
  const user = userStr ? JSON.parse(userStr) : null;
  const isSysadmin = user?.role === 'sysadmin';
  const [backups, setBackups] = useState<Backup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [settings, setSettings] = useState<BackupSettings | null>(null);
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [settingsError, setSettingsError] = useState<string | null>(null);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsForm, setSettingsForm] = useState<BackupSettings>({
    backup_enabled: true,
    backup_retention_days: 30,
  });

  const [creating, setCreating] = useState(false);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [restoringId, setRestoringId] = useState<string | null>(null);

  const [restoreBackupId, setRestoreBackupId] = useState('');
  const [restoreConfirm, setRestoreConfirm] = useState('');

  const token = localStorage.getItem('accessToken');
  const headers = {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
    'x-tenant-slug': localStorage.getItem('tenantSlug') || '',
  };

  function showSuccess(message: string) {
    setSuccess(message);
    setTimeout(() => setSuccess(null), 4000);
  }

  async function loadBackups() {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API}/backups`, { headers });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Error al cargar los backups');
      }
      const data = (await res.json()) as { data?: Backup[] };
      setBackups(data.data || []);
    } catch (e: any) {
      console.error('Backups load error:', e);
      setError(e.message || 'Error al cargar los backups');
    } finally {
      setLoading(false);
    }
  }

  async function loadSettings() {
    setSettingsLoading(true);
    setSettingsError(null);
    try {
      const res = await fetch(`${API}/backups/settings`, { headers });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Error al cargar la configuración');
      }
      const data = (await res.json()) as { data?: BackupSettings };
      const s = data.data || { backup_enabled: true, backup_retention_days: 30 };
      setSettings(s);
      setSettingsForm(s);
    } catch (e: any) {
      console.error('Backup settings load error:', e);
      setSettingsError(e.message || 'Error al cargar la configuración');
    } finally {
      setSettingsLoading(false);
    }
  }

  useEffect(() => {
    loadBackups();
    loadSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function createBackup() {
    setCreating(true);
    setError(null);
    try {
      const res = await fetch(`${API}/backups/create`, {
        method: 'POST',
        headers,
        body: JSON.stringify({}),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Error al crear el backup');
      }
      const data = (await res.json()) as { message?: string };
      showSuccess(data.message || 'Backup creado correctamente');
      await loadBackups();
    } catch (e: any) {
      setError(e.message || 'Error al crear el backup');
    } finally {
      setCreating(false);
    }
  }

  async function downloadBackup(id: string) {
    setDownloadingId(id);
    setError(null);
    try {
      const res = await fetch(`${API}/backups/${id}/download`, { headers });
      if (!res.ok) throw new Error('Error al descargar el backup');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const filename = res.headers.get('content-disposition')?.match(/filename="?([^"]+)"?/)?.[1] || `backup-${id}.json`;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      setError(e.message || 'Error al descargar el backup');
    } finally {
      setDownloadingId(null);
    }
  }

  async function deleteBackup(id: string) {
    if (!window.confirm('¿Eliminar este backup? Esta acción no se puede deshacer.')) return;
    setDeletingId(id);
    setError(null);
    try {
      // DELETE sin body: NO mandar Content-Type (fastify 400 si va JSON vacío)
      const delHeaders = { ...headers } as Record<string, string>;
      delete delHeaders['Content-Type'];
      const res = await fetch(`${API}/backups/${id}`, { method: 'DELETE', headers: delHeaders });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Error al eliminar el backup');
      }
      showSuccess('Backup eliminado');
      await loadBackups();
    } catch (e: any) {
      setError(e.message || 'Error al eliminar el backup');
    } finally {
      setDeletingId(null);
    }
  }

  async function restoreBackup() {
    if (!restoreBackupId) return;
    setRestoringId(restoreBackupId);
    setError(null);
    try {
      const res = await fetch(`${API}/backups/restore`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ backupId: restoreBackupId }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Error al restaurar el backup');
      }
      const data = (await res.json()) as { message?: string };
      showSuccess(data.message || 'Restauración completada');
      setRestoreBackupId('');
      setRestoreConfirm('');
      await loadBackups();
    } catch (e: any) {
      setError(e.message || 'Error al restaurar el backup');
    } finally {
      setRestoringId(null);
    }
  }

  async function saveSettings(e: React.FormEvent) {
    e.preventDefault();
    setSettingsSaving(true);
    setSettingsError(null);
    try {
      const res = await fetch(`${API}/backups/settings`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({
          backup_enabled: settingsForm.backup_enabled,
          backup_retention_days: Number(settingsForm.backup_retention_days),
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Error al guardar la configuración');
      }
      showSuccess('Configuración guardada');
      await loadSettings();
    } catch (e: any) {
      setSettingsError(e.message || 'Error al guardar la configuración');
    } finally {
      setSettingsSaving(false);
    }
  }

  const restoreReady = restoreBackupId && restoreConfirm === 'RESTAURAR';

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold">💾 Backups</h1>
          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-300 mt-1">
            Protegé los datos del comercio. Creá backups manuales, restaurá versiones anteriores y configurá copias automáticas.
          </p>
        </div>
        <Link
          to="/admin"
          className="inline-flex items-center justify-center gap-2 text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-800 min-h-[44px] px-2 rounded transition-colors"
        >
          ← Volver a Administración
        </Link>
      </div>

      {/* Alerts */}
      {success && (
        <div className="bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 p-3 rounded-lg mb-4 text-sm font-medium">
          ✓ {success}
        </div>
      )}
      {error && <ErrorBanner message={error} onRetry={() => { loadBackups(); loadSettings(); }} />}

      {/* Hero: create backup */}
      <section className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl p-5 sm:p-6 mb-6 text-white shadow-lg">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <p className="text-sm sm:text-base font-medium opacity-90">Copia de seguridad manual</p>
            <p className="text-xl sm:text-2xl font-bold mt-1">
              {backups.length > 0 ? `${backups.length} backup${backups.length === 1 ? '' : 's'} disponible${backups.length === 1 ? '' : 's'}` : 'Aún no hay backups'}
            </p>
            <p className="text-sm opacity-80 mt-1">
              Incluye productos, ventas, clientes, proveedores, caja y configuración.
            </p>
          </div>
          <button
            onClick={createBackup}
            disabled={creating}
            className="w-full sm:w-auto flex items-center justify-center gap-2 bg-white dark:bg-gray-800 text-blue-700 dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/30 disabled:bg-blue-200 disabled:text-blue-400 rounded-xl px-6 py-4 font-bold text-lg shadow transition-colors min-h-[56px] sm:min-h-[64px]"
          >
            <span className="text-2xl">💾</span>
            {creating ? 'Creando backup...' : 'Crear Backup Ahora'}
          </button>
        </div>
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Backup list */}
        <section className="lg:col-span-2">
          <h2 className="text-lg font-bold mb-3">Historial de Backups</h2>

          {loading ? (
            <LoadingSpinner />
          ) : backups.length === 0 ? (
            <EmptyState
              message="No hay backups guardados."
              action={
                <button
                  onClick={createBackup}
                  disabled={creating}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-70 min-h-[44px]"
                >
                  Crear el primero
                </button>
              }
            />
          ) : (
            <>
              {/* Mobile cards */}
              <div className="grid grid-cols-1 gap-3 lg:hidden mb-6">
                {backups.map((b) => {
                  const busy = downloadingId === b.id || deletingId === b.id;
                  return (
                    <div key={b.id} className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
                      <div className="flex items-start justify-between gap-3 mb-2">
                        <div>
                          <p className="font-semibold text-gray-900">{b.type === 'auto' ? '🤖 Automático' : '👤 Manual'}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 dark:dark:text-gray-400">{formatDateTime(b.created_at)}</p>
                        </div>
                        <span
                          className={classNames(
                            'px-2.5 py-1 rounded-full text-xs font-semibold',
                            b.status === 'restored'
                              ? 'bg-purple-100 text-purple-700'
                              : b.status === 'failed'
                                ? 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300'
                                : 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300'
                          )}
                        >
                          {statusLabels[b.status] || b.status}
                        </span>
                      </div>
                      <div className="grid grid-cols-3 gap-2 text-sm mb-3">
                        <div>
                          <p className="text-gray-500 dark:text-gray-400 dark:dark:text-gray-400 text-xs">Tamaño</p>
                          <p className="font-medium">{formatBytes(b.file_size)}</p>
                        </div>
                        <div>
                          <p className="text-gray-500 dark:text-gray-400 dark:dark:text-gray-400 text-xs">Tablas</p>
                          <p className="font-medium">{formatNumber(b.tables_count)}</p>
                        </div>
                        <div>
                          <p className="text-gray-500 dark:text-gray-400 dark:dark:text-gray-400 text-xs">Registros</p>
                          <p className="font-medium">{formatNumber(b.records_count)}</p>
                        </div>
                      </div>
                      <div className="flex flex-col sm:flex-row gap-2">
                        <button
                          onClick={() => downloadBackup(b.id)}
                          disabled={busy || downloadingId !== null}
                          className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 transition-colors disabled:opacity-70 min-h-[44px]"
                        >
                          {downloadingId === b.id ? 'Descargando...' : '⬇️ Descargar'}
                        </button>
                        <button
                          onClick={() => deleteBackup(b.id)}
                          disabled={busy}
                          className="flex-1 px-4 py-3 bg-red-50 text-red-700 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-900/40 rounded-lg text-sm font-semibold transition-colors disabled:opacity-70 min-h-[44px]"
                        >
                          {deletingId === b.id ? 'Eliminando...' : '🗑️ Eliminar'}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Desktop table */}
              <div className="hidden lg:block bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-200">Fecha</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-200">Tipo</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-200">Estado</th>
                      <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700 dark:text-gray-200">Tamaño</th>
                      <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700 dark:text-gray-200">Tablas</th>
                      <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700 dark:text-gray-200">Registros</th>
                      <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700 dark:text-gray-200">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {backups.map((b) => {
                      const busy = downloadingId === b.id || deletingId === b.id;
                      return (
                        <tr key={b.id} className="border-t hover:bg-gray-50 dark:hover:bg-gray-700">
                          <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">{formatDateTime(b.created_at)}</td>
                          <td className="px-4 py-3 text-sm">{b.type === 'auto' ? '🤖 Automático' : '👤 Manual'}</td>
                          <td className="px-4 py-3 text-sm">
                            <span
                              className={classNames(
                                'px-2 py-1 rounded-full text-xs font-semibold',
                                b.status === 'restored'
                                  ? 'bg-purple-100 text-purple-700'
                                  : b.status === 'failed'
                                    ? 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300'
                                    : 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300'
                              )}
                            >
                              {statusLabels[b.status] || b.status}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right text-sm font-medium">{formatBytes(b.file_size)}</td>
                          <td className="px-4 py-3 text-right text-sm text-gray-600 dark:text-gray-300">{formatNumber(b.tables_count)}</td>
                          <td className="px-4 py-3 text-right text-sm text-gray-600 dark:text-gray-300">{formatNumber(b.records_count)}</td>
                          <td className="px-4 py-3 text-right whitespace-nowrap">
                            <div className="flex items-center justify-end gap-2">
                              <button
                                onClick={() => downloadBackup(b.id)}
                                disabled={busy || downloadingId !== null}
                                className="px-3 py-2 bg-blue-600 text-white rounded-lg text-xs font-semibold hover:bg-blue-700 transition-colors disabled:opacity-70 min-h-[44px]"
                              >
                                {downloadingId === b.id ? '...' : '⬇️ Descargar'}
                              </button>
                              <button
                                onClick={() => deleteBackup(b.id)}
                                disabled={busy}
                                className="px-3 py-2 bg-red-50 text-red-700 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-900/40 rounded-lg text-xs font-semibold transition-colors disabled:opacity-70 min-h-[44px]"
                              >
                                {deletingId === b.id ? '...' : '🗑️ Eliminar'}
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <p className="mt-4 text-sm text-gray-500 dark:text-gray-400 dark:dark:text-gray-400">
                {backups.length} backup{backups.length !== 1 ? 's' : ''} guardado{backups.length !== 1 ? 's' : ''}.
              </p>
            </>
          )}
        </section>

        {/* Settings + restore */}
        <section className="space-y-6">
          {/* Restore */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 sm:p-5 border-l-4 border-red-500">
            <h2 className="text-lg font-bold mb-2 text-red-700 dark:text-red-300">🚨 Restaurar Backup</h2>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
              Reemplazará todos los datos actuales por los del backup seleccionado. Esta acción no se puede deshacer.
            </p>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Backup a restaurar</label>
                <select
                  value={restoreBackupId}
                  onChange={(e) => {
                    setRestoreBackupId(e.target.value);
                    setRestoreConfirm('');
                  }}
                  disabled={loading || backups.length === 0 || restoringId !== null}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm min-h-[44px]"
                >
                  <option value="">Seleccionar backup...</option>
                  {backups.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.type === 'auto' ? 'Automático' : 'Manual'} — {formatDateTime(b.created_at)} ({formatBytes(b.file_size)})
                    </option>
                  ))}
                </select>
              </div>

              {restoreBackupId && (
                <div className="bg-red-50 rounded-lg p-4 space-y-3">
                  <p className="text-sm font-medium text-red-700 dark:text-red-300">
                    ⚠️ Advertencia: se borrarán los datos actuales y se reemplazarán por el backup seleccionado.
                  </p>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                      Escribí <span className="font-bold uppercase">RESTAURAR</span> para confirmar
                    </label>
                    <input
                      type="text"
                      value={restoreConfirm}
                      onChange={(e) => setRestoreConfirm(e.target.value)}
                      placeholder="RESTAURAR"
                      className="w-full px-3 py-2 border border-red-300 rounded-lg text-sm min-h-[44px]"
                    />
                  </div>
                  <button
                    onClick={restoreBackup}
                    disabled={!restoreReady || restoringId !== null}
                    className={classNames(
                      'w-full px-4 py-3 rounded-lg font-bold text-white min-h-[48px] transition-colors',
                      restoreReady && !restoringId
                        ? 'bg-red-600 hover:bg-red-700'
                        : 'bg-red-300 cursor-not-allowed'
                    )}
                  >
                    {restoringId ? 'Restaurando...' : '🔁 Restaurar Backup'}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Settings */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 sm:p-5">
            <h2 className="text-lg font-bold mb-1">⚙️ Configuración</h2>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">Copias automáticas y retención.</p>

            {settingsLoading ? (
              <div className="space-y-3 animate-pulse">
                <div className="h-10 bg-gray-200 rounded" />
                <div className="h-10 bg-gray-200 rounded" />
              </div>
            ) : settingsError ? (
              <div className="bg-red-50 text-red-700 dark:text-red-300 p-3 rounded-lg text-sm flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <span>{settingsError}</span>
                <button
                  onClick={loadSettings}
                  className="px-3 py-2 bg-red-600 text-white rounded-lg text-xs font-medium min-h-[44px]"
                >
                  Reintentar
                </button>
              </div>
            ) : (
              <form onSubmit={saveSettings} className="space-y-4">
                <label className="flex items-center justify-between gap-3 cursor-pointer">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-200">Backup automático diario</span>
                  <div
                    className={classNames(
                      'relative inline-flex h-7 w-12 items-center rounded-full transition-colors',
                      settingsForm.backup_enabled ? 'bg-green-500' : 'bg-gray-300'
                    )}
                  >
                    <input
                      type="checkbox"
                      checked={settingsForm.backup_enabled}
                      onChange={(e) => setSettingsForm({ ...settingsForm, backup_enabled: e.target.checked })}
                      className="sr-only"
                    />
                    <span
                      className={classNames(
                        'inline-block h-5 w-5 transform rounded-full bg-white dark:bg-gray-800 transition-transform',
                        settingsForm.backup_enabled ? 'translate-x-6' : 'translate-x-1'
                      )}
                    />
                  </div>
                </label>

                {isSysadmin ? (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                      Días de retención
                    </label>
                    <input
                      type="number"
                      min={7}
                      max={365}
                      value={settingsForm.backup_retention_days}
                      onChange={(e) => setSettingsForm({ ...settingsForm, backup_retention_days: Number(e.target.value) })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm min-h-[44px]"
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 dark:dark:text-gray-400 mt-1">Entre 7 y 365 días.</p>
                  </div>
                ) : (
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Retención de backups: {settingsForm.backup_retention_days} días · Solo la puede modificar el administrador de la plataforma.
                  </p>
                )}

                <button
                  type="submit"
                  disabled={settingsSaving}
                  className="w-full px-4 py-3 bg-green-600 hover:bg-green-700 disabled:bg-green-300 text-white rounded-lg font-semibold transition-colors min-h-[48px]"
                >
                  {settingsSaving ? 'Guardando...' : 'Guardar Configuración'}
                </button>
              </form>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
